import React, { useEffect, useState } from 'react';
import {
  CORP_PERMISSION_LIST,
  hasPermission,
  type CorpPermission,
} from '@astra/shared';
import {
  PALETTE,
  Card,
  Hint,
  SectionTitle,
  smallBtnStyle,
  inputStyle,
} from '../station/stationTheme';
import { localCorpUsername } from './useCorporationData';
import type {
  CorpDetail,
  CorpDistributionConfig,
  CorpPermissionRow,
  CorpVoteDetail,
} from './types';

interface CardCommon {
  busy: boolean;
}

export function WithdrawCard({
  corpId,
  busy,
  onWithdraw,
}: CardCommon & { corpId: string; onWithdraw: (args: { corpId: string; amount: number }) => void }) {
  const [amount, setAmount] = useState(0);
  const send = () => { if (amount > 0) onWithdraw({ corpId, amount: Math.floor(amount) }); };
  return (
    <Card>
      <SectionTitle>Withdraw from bank</SectionTitle>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          style={{ ...inputStyle, width: 140 }}
          type="number"
          min={1}
          value={amount}
          onChange={(e) => setAmount(Math.max(0, parseInt(e.target.value) || 0))}
        />
        <button disabled={busy} style={smallBtnStyle(busy)} onClick={send}>WITHDRAW</button>
      </div>
      <Hint>Requires the Withdraw permission (the manager always has it). Logged in the audit trail.</Hint>
    </Card>
  );
}

export function DistributionCard({
  corpId,
  cfg,
  busy,
  onSave,
}: CardCommon & {
  corpId: string;
  cfg: CorpDistributionConfig;
  onSave: (args: { corpId: string; percentage: number; periodDays: number }) => void;
}) {
  const [pct, setPct] = useState<number>(cfg.percentage ?? 0);
  const [days, setDays] = useState<number>(cfg.periodDays ?? 7);
  const save = () => onSave({ corpId, percentage: pct, periodDays: days });
  return (
    <Card>
      <SectionTitle>Owner distributions</SectionTitle>
      <Hint>Automatically pay this % of the bank to owners (split by ownership) every period.</Hint>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          style={{ ...inputStyle, width: 70 }}
          type="number"
          min={0}
          max={100}
          value={pct}
          onChange={(e) => setPct(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
        />
        <span style={{ color: PALETTE.DIM, fontSize: 11 }}>% every</span>
        <input
          style={{ ...inputStyle, width: 64 }}
          type="number"
          min={1}
          value={days}
          onChange={(e) => setDays(Math.max(1, parseInt(e.target.value) || 1))}
        />
        <span style={{ color: PALETTE.DIM, fontSize: 11 }}>days</span>
        <button disabled={busy} style={smallBtnStyle(busy)} onClick={save}>SAVE</button>
      </div>
      <Hint>{cfg.lastDistributionAt ? `Last paid: ${new Date(cfg.lastDistributionAt).toLocaleString()}` : 'Never paid yet.'}</Hint>
    </Card>
  );
}

export function VotingCard({
  detail,
  busy,
  onStart,
  onCast,
}: CardCommon & {
  detail: CorpDetail;
  onStart: (args: { corpId: string; nomineeName: string }) => void;
  onCast: (args: { voteId: string; decision: 'remove' | 'keep' }) => void;
}) {
  const me = localCorpUsername();
  const myOwner = (detail.owners ?? []).find((o) => o.playerName === me);
  const vote: CorpVoteDetail | null | undefined = detail.activeVote;
  const eligibleNominees = (detail.owners ?? []).filter((o) => o.playerId !== detail.managerPlayerId);
  const hasEligible = eligibleNominees.length > 0;
  // A manager removal vote needs a co-owner to nominate. If the caller is the
  // only owner, no nomination is possible regardless of their percentage.
  const canStart = !!myOwner && Number(myOwner.percentage) >= 10 && !vote && hasEligible;
  const [nominee, setNominee] = useState<string>(eligibleNominees[0]?.playerName ?? '');

  return (
    <Card>
      <SectionTitle>Manager removal vote</SectionTitle>
      {vote ? (
        <>
          <Hint>
            Nominee: <b style={{ color: PALETTE.LIGHT }}>{vote.nomineeName}</b> · remove {vote.removePct}% / 51% · ends {new Date(vote.expiresAt).toLocaleString()}
          </Hint>
          {myOwner ? (
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <button
                style={smallBtnStyle(busy)}
                disabled={busy}
                onClick={() => onCast({ voteId: vote.id, decision: 'remove' })}
              >
                REMOVE{vote.myBallot === 'remove' ? ' ✓' : ''}
              </button>
              <button
                style={smallBtnStyle(busy)}
                disabled={busy}
                onClick={() => onCast({ voteId: vote.id, decision: 'keep' })}
              >
                KEEP{vote.myBallot === 'keep' ? ' ✓' : ''}
              </button>
            </div>
          ) : (
            <Hint>Only owners vote.</Hint>
          )}
        </>
      ) : canStart ? (
        <>
          <Hint>You hold ≥10%, so you can call a vote to replace the manager. Pick the replacement.</Hint>
          <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
            <select
              style={{ ...inputStyle, flex: 1, minWidth: 0 }}
              value={nominee}
              onChange={(e) => setNominee(e.target.value)}
            >
              {eligibleNominees.map((o) => (
                <option key={o.playerId} value={o.playerName}>{o.playerName}</option>
              ))}
            </select>
            <button
              style={smallBtnStyle(busy || !nominee)}
              disabled={busy || !nominee}
              onClick={() => onStart({ corpId: detail.id, nomineeName: nominee })}
            >
              START VOTE
            </button>
          </div>
        </>
      ) : myOwner && !hasEligible ? (
        <Hint>
          No other owners to nominate — a manager-removal vote needs at least one co-owner besides
          the current manager. Bring in another owner first (transfer a share to another pilot).
        </Hint>
      ) : myOwner && Number(myOwner.percentage) < 10 ? (
        <Hint>You hold less than 10%, so you can't initiate a vote. Owners holding ≥10% can call one.</Hint>
      ) : (
        <Hint>No active vote. Owners holding ≥10% can call one to replace the manager.</Hint>
      )}
    </Card>
  );
}

interface PermissionCandidate {
  playerId: string;
  playerName: string;
  permissionFlags: number;
  isOwner: boolean;
  isMember: boolean;
  hasExplicitGrant: boolean;
}

export function PermissionsCard({
  corpId,
  detail,
  busy,
  onSetPermission,
}: CardCommon & {
  corpId: string;
  detail: CorpDetail;
  onSetPermission: (args: { corpId: string; targetUsername: string; flags: number }) => void;
}) {
  // Master-detail layout. The candidate list is the UNION of members + owners
  // + pilots with an explicit permission row (so revocations stay possible
  // even if the grantee has since left). The search input is a pure FILTER
  // over that list — not a free-text "invent any pilot" form. The manager is
  // intentionally excluded (super-user; doesn't need rows).
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const rows = detail.permissionRows ?? [];
  const candidates: PermissionCandidate[] = (() => {
    const byId = new Map<string, PermissionCandidate>();
    const rowsById = new Map(rows.map((r) => [r.playerId, r]));

    const add = (playerId: string, playerName: string, opts: { owner?: boolean; member?: boolean } = {}) => {
      if (playerId === detail.managerPlayerId) return; // manager is super-user; skip
      const existing = byId.get(playerId);
      if (existing) {
        existing.isOwner = existing.isOwner || !!opts.owner;
        existing.isMember = existing.isMember || !!opts.member;
        return;
      }
      const row = rowsById.get(playerId);
      byId.set(playerId, {
        playerId,
        playerName,
        permissionFlags: row?.permissionFlags ?? 0,
        isOwner: !!opts.owner,
        isMember: !!opts.member,
        hasExplicitGrant: !!row,
      });
    };

    (detail.owners ?? []).forEach((o) => add(o.playerId, o.playerName, { owner: true }));
    (detail.members ?? []).forEach((m) => add(m.playerId, m.playerName, { member: true }));
    rows.forEach((r) => add(r.playerId, r.playerName));
    return Array.from(byId.values());
  })();

  // Keep the selection valid as the candidate list mutates.
  useEffect(() => {
    if (candidates.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (!selectedId || !candidates.some((c) => c.playerId === selectedId)) {
      setSelectedId(candidates[0].playerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates.length]);

  const filtered = candidates.filter((c) => c.playerName.toLowerCase().includes(search.toLowerCase()));
  const selected = candidates.find((c) => c.playerId === selectedId) ?? null;

  const setFlags = (username: string, flags: number) =>
    onSetPermission({ corpId, targetUsername: username, flags });

  const toggle = (cand: PermissionCandidate, perm: CorpPermission) => {
    const next = hasPermission(cand.permissionFlags, perm) ? cand.permissionFlags & ~perm : cand.permissionFlags | perm;
    setFlags(cand.playerName, next);
  };

  // Special-case messaging: if the search matches part of the manager's own
  // name, explain why they don't appear in the list. Substring (not exact)
  // because users often type a partial name — "pep" should still resolve to
  // "PEPES" the manager and surface the "that's you" hint.
  const me = localCorpUsername();
  const trimmedSearch = search.trim();
  const typingSelf = trimmedSearch.length > 0
    && me.length > 0
    && me.toLowerCase().includes(trimmedSearch.toLowerCase());

  return (
    <Card>
      <SectionTitle>Permissions</SectionTitle>
      <Hint>Grant slices of your authority to other pilots. You (manager) always hold all permissions.</Hint>

      <div style={permsLayoutStyle}>
        {/* LEFT — pure search filter over corp members + owners. No
            "invent a player" form: only pilots already in the corp can have
            permissions. To grant someone new, add them as a member first. */}
        <div style={permsListColStyle}>
          <input
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
            placeholder="Search members & owners…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div style={permsListStyle}>
            {/* "That's you" wins over the empty / no-match messages so the
                user understands why their own name doesn't appear, regardless
                of whether other pilots exist in the corp. */}
            {typingSelf && (
              <Hint>
                That's you — as manager you already hold all permissions.
              </Hint>
            )}
            {!typingSelf && candidates.length === 0 && (
              <Hint>
                No other pilots in this corp. Add a member from the Members tab first.
              </Hint>
            )}
            {!typingSelf && candidates.length > 0 && filtered.length === 0 && (
              <Hint>
                No member or owner matches “{trimmedSearch}”. Only pilots already in this corp can be granted permissions.
              </Hint>
            )}
            {filtered.map((cand) => {
              const isSelected = cand.playerId === selectedId;
              const grantCount = CORP_PERMISSION_LIST.reduce(
                (n, p) => n + (hasPermission(cand.permissionFlags, p.flag) ? 1 : 0),
                0,
              );
              const roleTag =
                cand.isOwner && cand.isMember ? 'owner+member'
                : cand.isOwner ? 'owner'
                : cand.isMember ? 'member'
                : 'external';
              return (
                <button
                  key={cand.playerId}
                  onClick={() => setSelectedId(cand.playerId)}
                  style={permsListItemStyle(isSelected)}
                >
                  <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                    <span style={{ color: isSelected ? PALETTE.TEAL : PALETTE.LIGHT, fontSize: 11 }}>
                      {cand.playerName}
                    </span>
                    <span style={{ color: PALETTE.DIM, fontSize: 8, letterSpacing: '0.05em' }}>
                      {roleTag}
                    </span>
                  </span>
                  <span style={{ color: grantCount > 0 ? PALETTE.TEAL : PALETTE.DIM, fontSize: 9 }}>
                    {grantCount}/{CORP_PERMISSION_LIST.length}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* RIGHT — selected pilot's checkboxes */}
        <div style={permsDetailColStyle}>
          {!selected && (
            <Hint>Pick a pilot on the left to edit their permissions.</Hint>
          )}
          {selected && (
            <>
              <div style={permsDetailHeaderStyle}>
                <span style={{ color: PALETTE.LIGHT, fontSize: 12, fontWeight: 600 }}>
                  {selected.playerName}
                </span>
                <span style={{ color: PALETTE.DIM, fontSize: 10 }}>permissions</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {CORP_PERMISSION_LIST.map((p) => {
                  const on = hasPermission(selected.permissionFlags, p.flag);
                  return (
                    <label
                      key={p.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '6px 8px',
                        borderRadius: 4,
                        cursor: busy ? 'not-allowed' : 'pointer',
                        background: on ? `${PALETTE.TEAL}15` : 'transparent',
                        border: `1px solid ${on ? `${PALETTE.TEAL}55` : PALETTE.BORDER}`,
                      }}
                    >
                      <input
                        type="checkbox"
                        disabled={busy}
                        checked={on}
                        onChange={() => toggle(selected, p.flag)}
                      />
                      <span style={{ color: on ? PALETTE.LIGHT : PALETTE.DIM, fontSize: 11 }}>
                        {p.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

const permsLayoutStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(160px, 220px) 1fr',
  gap: 12,
  marginTop: 4,
};

const permsListColStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  minWidth: 0,
};

const permsListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  maxHeight: 220,
  overflowY: 'auto',
  border: `1px solid ${PALETTE.BORDER}`,
  borderRadius: 4,
  padding: 4,
  background: PALETTE.DARK,
};

const permsListItemStyle = (isSelected: boolean): React.CSSProperties => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '6px 8px',
  borderRadius: 3,
  border: 'none',
  background: isSelected ? `${PALETTE.TEAL}22` : 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: "'JetBrains Mono', monospace",
});

const permsDetailColStyle: React.CSSProperties = {
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const permsDetailHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  paddingBottom: 6,
  borderBottom: `1px solid ${PALETTE.BORDER}`,
};
